import React from "react"
import { TableRow, TableRowColumn, Toggle } from "material-ui"
import { ITimecardWeek, ITimecardEmployee, IPunchSetDocument, INoOperation, IRuleProperty, ITimecardDay } from "../../models"
import EmployeeTimecardTablePSCell from "./EmployeeTimecardTablePSCell"
import { grey200 } from "material-ui/styles/colors"
import AccountStore from "../../stores/AccountStore"
import { DateUtils } from "react-day-picker"
import moment from "moment"
import * as api from "../../api"
import AppStateStore from "../../stores/AppStateStore"

interface IProps {
	fullPunchSets: IPunchSetDocument[]
	timecardWeek: ITimecardWeek
	user: ITimecardEmployee
	showTimecardDetails: boolean
	noOps: INoOperation[]
	punchRuleProperties: IRuleProperty[]
	reloadTimecard(msg: string): void
}

const EmployeeTimecardCalculatedValuesRow: React.SFC<IProps> = (props: IProps) => {
	if (!AccountStore.getPunchRuleEnabled()) return null

	const userPunchRuleDailyProperties = (props.user.PunchRuleProperties || []).filter(item => item.Scope == 0)
	const hasPunchRuleProperties = userPunchRuleDailyProperties && userPunchRuleDailyProperties.length > 0 && props.timecardWeek.HasPunchSets

	let hasCalculatedPunchsets = false
	props.timecardWeek.Days.forEach(day => {
		hasCalculatedPunchsets = hasCalculatedPunchsets || day.PunchSets.filter(
			timecardPS => props.fullPunchSets.find(i => i.Id === timecardPS.Id).Calculated && 
			props.fullPunchSets.find(i => i.Id === timecardPS.Id).MasterPunchSetId === null
		).length > 0
	})

	if (!hasCalculatedPunchsets && !(props.noOps && props.noOps.length > 0) && !hasPunchRuleProperties) return null

	const sameDate = (date1String: string, date2String: string): boolean => {
		if (!date1String || !date2String) return false

		var date1 = moment(date1String)
		var date2 = moment(date2String)

		if (date1.isValid() && date2.isValid()) {
			return DateUtils.isSameDay(date1.toDate(), date2.toDate())
		}

		return true
	}

	const getPunchRulePropertyInstance = (punchRuleProperty: IRuleProperty, day: ITimecardDay): IRuleProperty => {
		let returnVal = null
		if (props.punchRuleProperties && props.punchRuleProperties.length > 0) {
			props.punchRuleProperties.forEach(rule => {
				if (sameDate(rule.DateTime, day.StartDayTime) && punchRuleProperty.Value === rule.Value) {
					returnVal = rule
				}
			})
		}

		return returnVal
	}

	const getPunchRulePropertyEnabled = (punchRuleProperty: IRuleProperty, day: ITimecardDay): boolean => {
		const rule = getPunchRulePropertyInstance(punchRuleProperty, day)
		if (!rule) {
			return false
		}

		return rule.Enabled
	}

	const getPunchRuleObjectAuditString = (punchRuleProperty: IRuleProperty, day: ITimecardDay): string => {
		let returnString = ""

		const ruleInstance = getPunchRulePropertyInstance(punchRuleProperty, day)
		if (ruleInstance && ruleInstance.ObjectAudit) {
			if (ruleInstance.ObjectAudit.LastModified) {
				returnString = `Last modified ${moment(ruleInstance.ObjectAudit.LastModified).format("h:mma MM/DD/YYYY")} by ${ruleInstance.ObjectAudit.LastModifiedByName}`
			}
		}

		return returnString
	}

	const getChildPunchSets = (fullPunchSets, punchSetId): IPunchSetDocument[] => {
		const childPunchSets = fullPunchSets.filter(i => i.MasterPunchSetId === punchSetId)
		return childPunchSets
	}
	
	return (
		<TableRow className="EmployeeTimecardTotalsRow">
			{props.timecardWeek.Days.map((timecardDay, i) =>
				<TableRowColumn
					key={i}
					style={{ backgroundColor: grey200, verticalAlign: "top", paddingTop: 15 }}
				>
					{userPunchRuleDailyProperties.length > 0 &&
						<div>
							<div style={{ marginTop: 10, marginBottom: 10, borderTop: "solid 1px #aaaaaa" }} />
							{userPunchRuleDailyProperties.map((punchRuleProperty: IRuleProperty, index) =>
								<div key={index} title={getPunchRuleObjectAuditString(punchRuleProperty, timecardDay)}>
									<Toggle
										toggled={getPunchRulePropertyEnabled(punchRuleProperty, timecardDay)}
										onToggle={async (e, value) => {
											AppStateStore.setProgressSnackbarContent("loading")
											await api.updatePunchRuleProperty(props.user.Id, {
												DisplayName: punchRuleProperty.DisplayName,
												DateTime: timecardDay.StartDayTime,
												Value: punchRuleProperty.Value,
												Enabled: value,
												Scope: punchRuleProperty.Scope,
												ObjectAudit: null
											})
											AppStateStore.setProgressSnackbarContent(null)
											props.reloadTimecard("Reload Timecard")
										}}
										labelPosition="right"
										label={punchRuleProperty.DisplayName}
									/>
								</div>
							)}
							<div style={{ marginTop: 10, marginBottom: 10, borderTop: "solid 1px #aaaaaa" }} />
						</div>
					}
					{timecardDay.PunchSets.filter(timecardPS => 
						props.fullPunchSets.find(i => i.Id === timecardPS.Id).Calculated &&
						props.fullPunchSets.find(i => i.Id === timecardPS.Id).MasterPunchSetId === null
					).map((timecardPS, index) =>
						<EmployeeTimecardTablePSCell
							key={index}
							day={timecardDay}
							childPunchSets={getChildPunchSets(props.fullPunchSets, timecardPS.Id)}
							timecardLocked={false}
							readonly={true}
							punchSet={props.fullPunchSets.find(i => i.Id === timecardPS.Id)}
							addPunch={null}
							user={props.user}
							onConfirmDelete={null}
							onTimeChange={null}
							reloadTimecard={null}
							showTimecardDetails={props.showTimecardDetails}
							fullPunchSets={props.fullPunchSets}
						/>
					)}

					{props.noOps && props.noOps.filter(item => sameDate(item.LogDateTime, timecardDay.StartDayTime)).map((noOpItem, index) =>
						<div title={noOpItem.Description} key={index} style={{ cursor: "pointer" }}>
							<i className="fa fa-circle-info" /> {noOpItem.Title}
						</div>
					)}
				</TableRowColumn>
			)}
		</TableRow>
	)
}

export default EmployeeTimecardCalculatedValuesRow
